import type { Theme } from '@ifretes/shared'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Tema em tres estados: claro, escuro e sistema (padrao).
 *
 * A escolha e persistida em localStorage (primeiro paint, ainda deslogado, ver
 * o script inline no index.html) e, para usuario autenticado, tambem no
 * servidor via PATCH /me/preferences — assim acompanha o login entre
 * dispositivos (SPEC 8.2).
 */

export const THEME_STORAGE_KEY = 'ifretes:theme'

interface ThemeContextValue {
  /** O que o usuario escolheu. */
  theme: Theme
  /** O que esta efetivamente aplicado agora ('system' ja resolvido). */
  resolved: 'light' | 'dark'
  setTheme: (t: Theme) => void
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function lerEscolha(): Theme {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    // localStorage indisponivel: cair no padrao e seguir.
  }
  return 'system'
}

function prefereEscuro(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

function resolver(t: Theme): 'light' | 'dark' {
  return t === 'system' ? (prefereEscuro() ? 'dark' : 'light') : t
}

function aplicarNoDocumento(t: Theme): void {
  const raiz = document.documentElement

  // 'system' NAO estampa data-theme: sem o atributo, o @media do CSS assume, e
  // o tema passa a acompanhar o SO ao vivo. Estampar 'light' aqui congelaria a
  // preferencia e quebraria justamente o modo 'sistema'.
  if (t === 'system') raiz.removeAttribute('data-theme')
  else raiz.setAttribute('data-theme', t)

  const escuro = resolver(t) === 'dark'
  const meta = document.querySelector('meta[name="theme-color"]:not([media])')
  meta?.setAttribute('content', escuro ? '#141e28' : '#1351b4')
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(lerEscolha)
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolver(lerEscolha()))

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t)
    setResolved(resolver(t))
    aplicarNoDocumento(t)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, t)
    } catch {
      // Sem persistencia a escolha vale so nesta aba. Melhor que falhar.
    }
  }, [])

  // Em modo 'sistema', acompanhar a mudanca do SO com a aba aberta.
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const aoMudar = () => {
      setResolved(prefereEscuro() ? 'dark' : 'light')
      aplicarNoDocumento('system')
    }
    mq.addEventListener('change', aoMudar)
    return () => mq.removeEventListener('change', aoMudar)
  }, [theme])

  // Ciclo do botao: claro -> escuro -> sistema -> claro.
  const toggle = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light')
  }, [theme, setTheme])

  const valor = useMemo(
    () => ({ theme, resolved, setTheme, toggle }),
    [theme, resolved, setTheme, toggle],
  )

  return <ThemeContext.Provider value={valor}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme precisa estar dentro de <ThemeProvider>')
  return ctx
}

/**
 * Aplica no cliente o tema que veio do servidor no login.
 *
 * Só sobrescreve a escolha local quando ainda não há uma: se a pessoa acabou de
 * trocar o tema neste dispositivo, a preferência salva no servidor (talvez de
 * meses atrás, em outro aparelho) não deve desfazer o gesto que ela acabou de
 * fazer.
 */
export function aplicarTemaDoServidor(t: Theme): void {
  try {
    if (localStorage.getItem(THEME_STORAGE_KEY)) return
    localStorage.setItem(THEME_STORAGE_KEY, t)
  } catch {
    /* segue com o tema em memoria */
  }
  aplicarNoDocumento(t)
}
